import { useState, useEffect } from "react"
import Header from "../common/Header.js"
import Sidebar from "../common/Sidebar.js"
import dashboardService from "../../services/DashboardService.js"
import userService from "../../services/UserService.js"
import Alert from "../common/Alert.js"
import {
  Users, MapPin, FileText, Image,
  TrendingUp, Filter, X, Box, Container,
  ClipboardCheck, Receipt, DollarSign, CreditCard,
  AlertCircle, CheckCircle2, IndianRupee, Landmark, Percent, Clock, Download,
  BarChart3, Activity, ShieldCheck, LayoutDashboard 
} from "lucide-react"
import {
  AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, BarChart, Bar,
  PieChart, Pie, Cell, RadialBarChart, RadialBar
} from 'recharts'

const COLORS = ['#F97316', '#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899']
const STATUS_COLORS = { paid: '#10B981', unpaid: '#EF4444', partial_paid: '#F59E0B', pending: '#F59E0B' }

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl shadow-xl p-3 text-sm">
        <p className="font-semibold text-gray-700 mb-2">{label}</p>
        {payload.map((p, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full" style={{ background: p.color }} />
            <span className="text-gray-600">{p.name}:</span>
            <span className="font-bold text-gray-900">{p.value}</span>
          </div>
        ))}
      </div>
    )
  }
  return null
}

const StatCard = ({ title, value, subtitle, icon: Icon, gradient, textLight }) => (
  <div className={`${gradient} rounded-md shadow-lg p-5 py-3 text-white relative overflow-hidden`}>
    <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-white opacity-5 -translate-y-8 translate-x-8" />
    <div className="relative">
      <div className="flex items-center justify-between mb-4">
        <p className={`${textLight} text-sm font-medium`}>{title}</p>
        <div className="bg-white bg-opacity-20 p-2.5 rounded-sm">
          <Icon size={22} />
        </div>
      </div>
      <h3 className="text-3xl font-bold">{value}</h3>
      {subtitle && <p className={`${textLight} text-xs mt-1`}>{subtitle}</p>}
    </div>
  </div>
)

const RecordCard = ({ title, total, published, unpublished, icon: Icon, color, borderColor }) => (
  <div className={`bg-white rounded-md shadow-sm border border-gray-100 p-5 py-3 border-l-4 ${borderColor}`}>
    <div className="flex items-center gap-3 mb-3">
      <div className={`p-2 rounded-lg bg-opacity-10 ${color.replace('text-', 'bg-')}`}>
        <Icon size={20} className={color} />
      </div>
      <h4 className="font-semibold text-gray-500 text-sm">{title}</h4>
    </div>
    <p className="text-2xl font-semibold text-gray-600 mb-2">{total}</p>
    <div className="flex gap-3">
      <span className="flex items-center gap-1 text-xs bg-green-50 text-green-700 px-2 py-1 rounded-full font-medium">
        <CheckCircle2 size={11} /> {published} Published
      </span>
      <span className="flex items-center gap-1 text-xs bg-gray-50 text-gray-500 px-2 py-1 rounded-full font-medium">
        <Clock size={11} /> {unpublished} Draft
      </span>
    </div>
  </div>
)

const SectionHeader = ({ icon: Icon, title, color = "text-orange" }) => (
  <div className="flex items-center gap-2 mb-4 bg-gray-100 p-2 px-1">
    <div className="w-1 h-6 rounded-full bg-orange-500" />
    <Icon size={20} className={color} />
    <h2 className="text-lg font-semibold text-gray-600">{title}</h2>
  </div>
)

const AdminDashboard = () => {
  const [loading, setLoading] = useState(true)
  const [dashboardData, setDashboardData] = useState(null)
  const [clients, setClients] = useState([])
  const [filters, setFilters] = useState({ from_date: '', to_date: '', client_id: '' })
  const [showFilters, setShowFilters] = useState(false)
  const [datePreset, setDatePreset] = useState('')

  useEffect(() => {
    const userRole = dashboardService.getCurrentUserRole()
    const userId = dashboardService.getCurrentUserId()
    console.log('[v0] AdminDashboard initialized - Role:', userRole, '- User ID:', userId)
    
    fetchDashboardData()
    fetchClients()
  }, [])

  const fetchDashboardData = async (customFilters = filters) => {
    try {
      setLoading(true)
      const response = await dashboardService.getDashboardData(customFilters)
      setDashboardData(response.data)
    } catch (err) {
      Alert.error('Failed to load dashboard data')
    } finally {
      setLoading(false)
    }
  }

  const fetchClients = async () => {
    try {
      const response = await userService.getClients(1, 100)
      setClients(response.data || [])
    } catch (err) {
      console.error('Failed to load clients', err)
    }
  }

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value }
    setFilters(newFilters)
    if (field !== 'client_id') setDatePreset('')
  }

  const handleDatePresetChange = (preset) => {
    if (!preset) { clearFilters(); return }
    const presets = dashboardService.getDatePresets()
    const selected = presets[preset]
    if (selected) {
      const newFilters = { ...filters, from_date: selected.from, to_date: selected.to }
      setFilters(newFilters)
      setDatePreset(preset)
      fetchDashboardData(newFilters)
    }
  }

  const applyFilters = () => { fetchDashboardData(); setShowFilters(false) }

  const clearFilters = () => {
    const cleared = { from_date: '', to_date: '', client_id: '' }
    setFilters(cleared)
    setDatePreset('')
    fetchDashboardData(cleared)
  }

  const exportTIReport = () => {
    const ti = dashboardData?.ti_report
    if (!ti) return
    const csv = [
      ['Metric', 'Value'],
      ['Total TI Reports', ti.total],
      ['Published', ti.published],
      ['Draft / Unpublished', ti.unpublished],
    ].map(r => r.join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = 'ti_report_summary.csv'; a.click()
    URL.revokeObjectURL(url)
  }

  const hasActiveFilters = filters.from_date || filters.to_date || filters.client_id

  // ─── Chart data helpers ─────────────────────────────────────────────────
  const prepareAreaChartData = () => {
    if (!dashboardData?.records_by_date) return []
    const dateMap = new Map()
    Object.entries(dashboardData.records_by_date).forEach(([key, data]) => {
      data.forEach(item => {
        if (!dateMap.has(item.date)) dateMap.set(item.date, { date: item.date })
        const label = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
        dateMap.get(item.date)[label] = item.count
      })
    })
    return Array.from(dateMap.values()).sort((a, b) => new Date(a.date) - new Date(b.date))
  }

  const prepareDonutData = () => {
    if (!dashboardData) return []
    return [
      { name: 'Dry Box Survey', value: dashboardData.dry_box_survey?.total || 0 },
      { name: 'Tank Cleaning', value: dashboardData.tank_cleaning?.total || 0 },
      { name: 'On Hire', value: dashboardData.onhire?.total || 0 },
      { name: 'EI Receipt', value: dashboardData.ei_receipt?.total || 0 },
      { name: 'TI Report', value: dashboardData.ti_report?.total || 0 },
    ]
  }

  const preparePublishedBar = () => {
    if (!dashboardData) return []
    return [
      { name: 'Dry Box', published: dashboardData.dry_box_survey?.published || 0, unpublished: dashboardData.dry_box_survey?.unpublished || 0 },
      { name: 'Tank Clean', published: dashboardData.tank_cleaning?.published || 0, unpublished: dashboardData.tank_cleaning?.unpublished || 0 },
      { name: 'On Hire', published: dashboardData.onhire?.published || 0, unpublished: dashboardData.onhire?.unpublished || 0 },
      { name: 'EI Receipt', published: dashboardData.ei_receipt?.published || 0, unpublished: dashboardData.ei_receipt?.unpublished || 0 },
      { name: 'TI Report', published: dashboardData.ti_report?.published || 0, unpublished: dashboardData.ti_report?.unpublished || 0 },
    ]
  }

  const prepareUsersChart = () => {
    if (!dashboardData?.users_by_role) return []
    return Object.entries(dashboardData.users_by_role).map(([role, count]) => ({ name: role, value: count }))
  }

  const prepareBillingTrend = () => {
    if (!dashboardData?.records_by_date?.billing) return []
    return dashboardData.records_by_date.billing.map(item => ({ date: item.date, invoices: item.count }))
  }

  const prepareBillingStatusData = () => {
    const b = dashboardData?.billing
    if (!b) return []
    return [
      { name: 'Paid', value: b.paid, fill: '#10B981' },
      { name: 'Unpaid', value: b.unpaid, fill: '#EF4444' },
      { name: 'Partial', value: b.partial_paid, fill: '#F59E0B' },
    ]
  }

  const prepareTIStatusData = () => {
    const ti = dashboardData?.ti_report
    if (!ti) return []
    return [
      { name: 'Published', value: ti.published, fill: '#10B981' },
      { name: 'Draft', value: ti.unpublished, fill: '#94A3B8' },
    ]
  }

  const preparePaymentStatusBar = () => {
    const ps = dashboardData?.payment_status_summary
    if (!ps) return []
    return Object.entries(ps).map(([svc, stats]) => ({
      name: svc.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      paid: stats.paid,
      pending: stats.pending,
      unpaid: stats.unpaid,
    }))
  }

  const billing = dashboardData?.billing || {}
  const ti = dashboardData?.ti_report || {}

  if (loading && !dashboardData) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-orange-500 mx-auto mb-4" />
          <p className="text-gray-500 font-medium">Loading dashboard…</p>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-gray-50 w-full">
          <div className="flex-1 flex flex-col">
            <main className="flex-1 p-6 overflow-auto">

              {/* ── Page header ── */}
              <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
                <div>
                  <h1 className="text-2xl sm:text-2xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                    <LayoutDashboard className="text-orange" size={28} />
                    Admin Dashboard
                  </h1>
                </div>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-sm text-sm font-medium transition-all ${
                    hasActiveFilters ? 'bg-orange-500 text-white shadow-md' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <Filter size={16} />
                  Filters
                  {hasActiveFilters && <span className="bg-white text-orange-500 px-1.5 py-0.5 rounded-full text-xs font-bold">Active</span>}
                </button>
              </div>

              {/* ── Filter panel ── */}
              {showFilters && (
                <div className="bg-white rounded-sm shadow-sm border border-gray-100 p-6 mb-8">
                  <div className="flex items-center justify-between mb-5">
                    <h3 className="font-bold text-gray-800">Dashboard Filters</h3>
                    <button onClick={() => setShowFilters(false)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-5">
                    {[
                      { label: 'Date Preset', el: (
                        <select value={datePreset} onChange={e => handleDatePresetChange(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent">
                          <option value="">Custom Range</option>
                          {['today','yesterday','last7Days','last30Days','last3Months','last6Months','thisMonth','thisYear','lastYear'].map(k => (
                            <option key={k} value={k}>{dashboardService.getDatePresets()[k]?.label}</option>
                          ))}
                        </select>
                      )},
                      { label: 'From Date', el: <input type="date" value={filters.from_date} onChange={e => handleFilterChange('from_date', e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent" /> },
                      { label: 'To Date', el: <input type="date" value={filters.to_date} onChange={e => handleFilterChange('to_date', e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent" /> },
                      { label: 'Filter by Client', el: (
                        <select value={filters.client_id} onChange={e => handleFilterChange('client_id', e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 focus:border-transparent">
                          <option value="">All Clients</option>
                          {clients.map(c => <option key={c.user_id} value={c.user_id}>{c.name}</option>)}
                        </select>
                      )},
                    ].map(({ label, el }) => (
                      <div key={label}>
                        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">{label}</label>
                        {el}
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-3 justify-end">
                    <button onClick={clearFilters} className="px-4 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm hover:bg-gray-50">Clear</button>
                    <button onClick={applyFilters} className="px-4 py-2 bg-buttonBg text-white rounded-xl text-sm hover:bg-orange-600 font-medium">Apply Filters</button>
                  </div>
                </div>
              )}

              {/* ══════════════════════════════════════
                  SECTION 1 – SYSTEM OVERVIEW
              ══════════════════════════════════════ */}
              <SectionHeader icon={Activity} title="System Overview" />
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
                <StatCard title="Total Users" value={dashboardData?.total_users || 0} subtitle="Across all roles" icon={Users} gradient="bg-buttonBg" textLight="text-orange-100" />
                <StatCard title="Total Locations" value={dashboardData?.total_locations || 0} subtitle="Active depots" icon={MapPin} gradient="bg-gradient-to-br from-blue-500 to-blue-600" textLight="text-blue-100" />
                <StatCard title="Total Tanks" value={dashboardData?.total_tanks || 0} subtitle="All clients" icon={Box} gradient="bg-gradient-to-br from-green-500 to-green-600" textLight="text-green-100" />
                <StatCard title="Total Containers" value={dashboardData?.total_containers || 0} subtitle="All clients" icon={Container} gradient="bg-gradient-to-br from-purple-500 to-purple-600" textLight="text-purple-100" />
              </div>

              {/* Record type breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
                <RecordCard title="Dry Box Survey" total={dashboardData?.dry_box_survey?.total || 0} published={dashboardData?.dry_box_survey?.published || 0} unpublished={dashboardData?.dry_box_survey?.unpublished || 0} icon={FileText} color="text-orange-500" borderColor="border-orange-500" />
                <RecordCard title="Tank Cleaning" total={dashboardData?.tank_cleaning?.total || 0} published={dashboardData?.tank_cleaning?.published || 0} unpublished={dashboardData?.tank_cleaning?.unpublished || 0} icon={ClipboardCheck} color="text-blue-500" borderColor="border-blue-500" />
                <RecordCard title="On Hire" total={dashboardData?.onhire?.total || 0} published={dashboardData?.onhire?.published || 0} unpublished={dashboardData?.onhire?.unpublished || 0} icon={Box} color="text-green-500" borderColor="border-green-500" />
                <RecordCard title="EI Receipt" total={dashboardData?.ei_receipt?.total || 0} published={dashboardData?.ei_receipt?.published || 0} unpublished={dashboardData?.ei_receipt?.unpublished || 0} icon={Receipt} color="text-purple-500" borderColor="border-purple-500" />
                <RecordCard title="TI Report" total={dashboardData?.ti_report?.total || 0} published={dashboardData?.ti_report?.published || 0} unpublished={dashboardData?.ti_report?.unpublished || 0} icon={ShieldCheck} color="text-teal-500" borderColor="border-teal-500" />
              </div>

              {/* Charts Row 1 */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                <div className="lg:col-span-2 bg-white rounded-sm shadow-sm border border-gray-100 p-6">
                  <div className="flex items-center gap-2 mb-5">
                    <TrendingUp size={18} className="text-orange" />
                    <h3 className="font-semibold text-gray-500">Records Over Time</h3>
                  </div>
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={prepareAreaChartData()} margin={{ top: 5, right: 10, left: 0, bottom: 60 }}>
                      <defs>
                        {[['dry','#F97316'],['tank','#3B82F6'],['hire','#10B981'],['ei','#8B5CF6'],['ti','#14B8A6']].map(([k,c]) => (
                          <linearGradient key={k} id={`grad${k}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={c} stopOpacity={0.15}/>
                            <stop offset="95%" stopColor={c} stopOpacity={0}/>
                          </linearGradient>
                        ))}
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} angle={-40} textAnchor="end" height={70} tickLine={false} />
                      <YAxis
                        tick={{ fontSize: 11 }}
                        tickLine={false}
                        axisLine={false}
                        domain={[0, (dataMax) => dataMax + 1]}s
                        allowDecimals={false}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend iconType="circle" iconSize={8} />
                      <Area type="monotone" dataKey="Dry Box Survey" stroke="#F97316" strokeWidth={2} fill="url(#graddry)" dot={{ r: 3, fill: '#F97316' }} activeDot={{ r: 5 }} connectNulls={true} />
                      <Area type="monotone" dataKey="Tank Cleaning" stroke="#3B82F6" strokeWidth={2} fill="url(#gradtank)" dot={{ r: 3, fill: '#3B82F6' }} activeDot={{ r: 5 }} connectNulls={true} />
                      <Area type="monotone" dataKey="Onhire" stroke="#10B981" strokeWidth={2} fill="url(#gradhire)" dot={{ r: 3, fill: '#10B981' }} activeDot={{ r: 5 }} connectNulls={true} />
                      <Area type="monotone" dataKey="Ei Receipt" stroke="#8B5CF6" strokeWidth={2} fill="url(#gradei)" dot={{ r: 3, fill: '#8B5CF6' }} activeDot={{ r: 5 }} connectNulls={true} />
                      <Area type="monotone" dataKey="Ti Report" stroke="#14B8A6" strokeWidth={2} fill="url(#gradti)" dot={{ r: 3, fill: '#14B8A6' }} activeDot={{ r: 5 }} connectNulls={true} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-white rounded-sm shadow-sm border border-gray-100 p-6">
                  <h3 className="font-semibold text-gray-500 mb-5">Record Distribution</h3>
                  <ResponsiveContainer width="100%" height={180}>
                    <PieChart>
                      <Pie data={prepareDonutData()} cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={3} dataKey="value">
                        {prepareDonutData().map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-2 mt-4">
                    {prepareDonutData().map((d, i) => (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full" style={{ background: COLORS[i] }} />
                          <span className="text-gray-600">{d.name}</span>
                        </div>
                        <span className="font-semibold text-gray-800">{d.value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Charts Row 2 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                <div className="bg-white rounded-sm shadow-sm border border-gray-100 p-6">
                  <h3 className="font-semibold text-gray-500 mb-5">Published vs Draft by Record Type</h3>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={preparePublishedBar()} barCategoryGap="30%">
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} tickLine={false} />
                      <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend iconType="circle" iconSize={8} />
                      <Bar dataKey="published" fill="#10B981" name="Published" radius={[4,4,0,0]} />
                      <Bar dataKey="unpublished" fill="#f65600" name="Draft" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-white rounded-sm shadow-sm border border-gray-100 p-6">
                  <h3 className="font-semibold text-gray-500 mb-5">Users by Role</h3>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={prepareUsersChart()} layout="vertical" barCategoryGap="30%">
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                      <YAxis dataKey="name" type="category" width={160} tick={{ fontSize: 11 }} tickLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar dataKey="value" name="Users" fill="#F97316" radius={[0,4,4,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* Payment Status by Service */}
              {preparePaymentStatusBar().length > 0 && (
                <div className="bg-white rounded-sm shadow-sm border border-gray-100 p-6 mb-8">
                  <h3 className="font-semibold text-gray-500 mb-5">Payment Status Across All Services</h3>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={preparePaymentStatusBar()} barCategoryGap="25%">
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} tickLine={false} />
                      <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend iconType="circle" iconSize={8} />
                      <Bar dataKey="paid" fill="#10B981" name="Paid" radius={[4,4,0,0]} />
                      <Bar dataKey="pending" fill="#F59E0B" name="Pending" radius={[4,4,0,0]} />
                      <Bar dataKey="unpaid" fill="#EF4444" name="Unpaid" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* ══════════════════════════════════════
                  SECTION 3 – BILLING
              ══════════════════════════════════════ */}
              <SectionHeader icon={IndianRupee} title="Billing Overview" color="text-green-600" />
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-6">
                <StatCard title="Total Invoices" value={billing.total || 0} subtitle="All clients" icon={FileText} gradient="bg-gradient-to-br from-slate-600 to-slate-700" textLight="text-slate-300" />
                <StatCard title="Total Billed" value={`₹${(billing.total_billed_amount || 0).toLocaleString()}`} subtitle="Gross amount" icon={CreditCard} gradient="bg-gradient-to-br from-blue-600 to-blue-700" textLight="text-blue-200" />
                <StatCard title="Total Received" value={`₹${(billing.total_paid_amount || 0).toLocaleString()}`} subtitle="Payments collected" icon={CheckCircle2} gradient="bg-gradient-to-br from-green-600 to-green-700" textLight="text-green-200" />
                <StatCard title="Outstanding Balance" value={`₹${(billing.total_balance_amount || 0).toLocaleString()}`} subtitle="Pending collection" icon={AlertCircle} gradient="bg-gradient-to-br from-red-500 to-red-600" textLight="text-red-100" />
              </div>

              {/* TDS + Bank Payment breakdown */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
                <StatCard title="Total Bank Payment" value={`₹${(billing.total_bank_payment || 0).toLocaleString()}`} subtitle="Received via bank" icon={Landmark} gradient="bg-gradient-to-br from-indigo-500 to-indigo-700" textLight="text-indigo-200" />
                <StatCard title="Total TDS" value={`₹${(billing.total_tds || 0).toLocaleString()}`} subtitle="Tax deducted at source" icon={Percent} gradient="bg-gradient-to-br from-purple-500 to-purple-700" textLight="text-purple-200" />
              </div>

              {/* Invoice status badges */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                {[
                  { label: 'Paid Invoices', count: billing.paid || 0, color: 'bg-green-50 border-green-200 text-green-700', dot: 'bg-green-500' },
                  { label: 'Partially Paid', count: billing.partial_paid || 0, color: 'bg-yellow-50 border-yellow-200 text-yellow-700', dot: 'bg-yellow-500' },
                  { label: 'Unpaid Invoices', count: billing.unpaid || 0, color: 'bg-red-50 border-red-200 text-red-700', dot: 'bg-red-500' },
                ].map(({ label, count, color, dot }) => (
                  <div key={label} className={`border rounded-2xl p-5 ${color}`}>
                    <div className="flex items-center gap-2 mb-1">
                      <div className={`w-2 h-2 rounded-full ${dot}`} />
                      <span className="text-xs font-semibold uppercase tracking-wide">{label}</span>
                    </div>
                    <p className="text-3xl font-bold">{count}</p>
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
                <div className="lg:col-span-2 bg-white rounded-sm shadow-sm border border-gray-100 p-6">
                  <h3 className="font-semibold text-gray-500 mb-5">Monthly Billing Trend</h3>
                  <ResponsiveContainer width="100%" height={260}>
                    <AreaChart data={prepareBillingTrend()} margin={{ top: 5, right: 10, left: 0, bottom: 60 }}>
                      <defs>
                        <linearGradient id="gradBilling" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.2}/>
                          <stop offset="95%" stopColor="#3B82F6" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} angle={-40} textAnchor="end" height={70} tickLine={false} />
                      <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} domain={[0, (dataMax) => dataMax + 1]} allowDecimals={false} />
                      <Tooltip content={<CustomTooltip />} />
                      <Area type="monotone" dataKey="invoices" stroke="#3B82F6" strokeWidth={2.5} fill="url(#gradBilling)" dot={{ r: 3, fill: '#3B82F6' }} activeDot={{ r: 5 }} name="Invoices" connectNulls={true} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                <div className="bg-white rounded-sm shadow-sm border border-gray-100 p-6">
                  <h3 className="font-semibold text-gray-500 mb-4">Invoice Status Mix</h3>
                  <ResponsiveContainer width="100%" height={170}>
                    <PieChart>
                      <Pie data={prepareBillingStatusData()} cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={4} dataKey="value">
                        {prepareBillingStatusData().map((d, i) => <Cell key={i} fill={d.fill} />)}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="space-y-3 mt-4">
                    {prepareBillingStatusData().map(d => (
                      <div key={d.name} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ background: d.fill }} />
                          <span className="text-sm text-gray-600">{d.name}</span>
                        </div>
                        <span className="text-sm font-bold text-gray-800">{d.value}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-5 pt-5 border-t border-gray-100">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Collection Rate</span>
                      <span className="font-bold text-gray-900">
                        {billing.total_billed_amount > 0
                          ? `${((billing.total_paid_amount / billing.total_billed_amount) * 100).toFixed(1)}%`
                          : '0%'}
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2 mt-2">
                      <div
                        className="bg-green-500 h-2 rounded-full transition-all"
                        style={{ width: billing.total_billed_amount > 0 ? `${Math.min((billing.total_paid_amount / billing.total_billed_amount) * 100, 100)}%` : '0%' }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* ── File Cabinet Images ── */}
              <div className="bg-white rounded-sm shadow-sm border border-gray-100 p-6">
                <div className="flex items-center gap-2 mb-5">
                  <Image size={18} className="text-orange" />
                  <h3 className="font-semibold text-gray-500">File Cabinet Images</h3>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
                  {[
                    { label: 'Dry Box', val: dashboardData?.file_cabinet_images?.dry_box, bg: 'bg-orange-50', text: 'text-orange-600' },
                    { label: 'Tank Cleaning', val: dashboardData?.file_cabinet_images?.tank_cleaning, bg: 'bg-blue-50', text: 'text-blue-600' },
                    { label: 'On Hire', val: dashboardData?.file_cabinet_images?.onhire, bg: 'bg-green-50', text: 'text-green-600' },
                    { label: 'EI Receipt', val: dashboardData?.file_cabinet_images?.ei_receipt, bg: 'bg-purple-50', text: 'text-purple-600' },
                    { label: 'TI Report', val: dashboardData?.file_cabinet_images?.ti_report, bg: 'bg-teal-50', text: 'text-teal-600' },
                    { label: 'Total', val: dashboardData?.file_cabinet_images?.total, bg: 'bg-gray-100', text: 'text-gray-900', bold: true },
                  ].map(({ label, val, bg, text, bold }) => (
                    <div key={label} className={`text-center p-4 ${bg} rounded-xl`}>
                      <p className={`text-2xl font-bold ${text}`}>{val || 0}</p>
                      <p className={`text-xs mt-1 ${bold ? 'font-bold text-gray-700' : 'text-gray-500'}`}>{label}</p>
                    </div>
                  ))}
                </div>
              </div>

            </main>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminDashboard
